import type { GraphQLError } from "graphql";
import type { UpstreamHttpErrorExtentions } from "@charterindex/halyard";


/**
 * Error thrown when a {@link Query} encounters a {@link GraphQLError} which
 * it did not handle.
 */
export class UnhandledGraphQLError extends Error {
	/**
	 * The GraphQL error which caused this to be thrown.
	 */
	readonly graphQLError: Readonly<GraphQLError>;

	constructor(graphQLError: Readonly<GraphQLError>) {
		super(graphQLError.message, graphQLError);
		this.graphQLError = graphQLError;
		this.name = "UnhandledGraphQLError";
	}
}

export const isGraphqlError = (x: unknown): x is GraphQLError & { extensions: { code: string; }; } => (
	!!x && typeof (x) === "object"
	&& "extensions" in x && !!x["extensions"] && typeof (x["extensions"]) === "object"
	&& "code" in x.extensions && typeof (x.extensions.code) === "string"
);

export const isGraphqlNotFoundError = (x: unknown): boolean => {
	if(x instanceof UnhandledGraphQLError) {
		return isGraphqlNotFoundError(x.graphQLError);
	}

	return isGraphqlError(x) && x.extensions.code === "NOT_FOUND";
};

type UnhandledUpstreamError = UnhandledGraphQLError & {
	graphQLError: Readonly<GraphQLError> & {
		extensions: Readonly<UpstreamHttpErrorExtentions>;
	};
};

export const isUnhandledUpstreamError = (x: unknown): x is UnhandledUpstreamError => {
	if(!(x instanceof UnhandledGraphQLError)) {
		return false;
	}

	return x.graphQLError.extensions["code"] === "UPSTREAM";
};

export const isUpstream403Error = (x: unknown): x is UnhandledUpstreamError => {
	return isGraphqlError(x) && x.extensions.code === "UPSTREAM" && x.extensions["statusCode"] === 403;
};
